import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { InputControl, TextareaControl } from "@shared/components/form";
import { Button } from "@shared/components/ui/Button";
import { ChevronDown, ChevronUp, GripVertical, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { CreatePollData, Poll, PollVoterVisibility } from "../types/poll.types";
import {
  DEFAULT_VOTER_VISIBILITY,
  MAX_OPTION_LENGTH,
  MAX_OPTIONS,
  MAX_QUESTIONS,
  MIN_OPTIONS,
  toCreatePollData,
  toDate,
} from "../utils/poll.utils";

interface PollCreatorProps {
  /** The poll being edited, if there is one. */
  initialPoll?: Poll | CreatePollData | null;
  onCancel: () => void;
  onSave: (data: CreatePollData) => void;
  /** Where the poll goes, named in the heading and the save button. */
  ownerLabel?: string;
}

/** Rows carry a key of their own: ids only exist once a poll is saved. */
interface DraftOption {
  key: string;
  id?: string;
  text: string;
}

interface DraftQuestion {
  key: string;
  id?: string;
  text: string;
  allowMultiple: boolean;
  options: DraftOption[];
}

let nextKey = 0;
const newKey = () => `draft-${nextKey++}`;

const emptyOption = (): DraftOption => ({ key: newKey(), text: "" });

const emptyQuestion = (): DraftQuestion => ({
  key: newKey(),
  text: "",
  allowMultiple: false,
  options: [emptyOption(), emptyOption()],
});

function toDraft(poll: Poll | CreatePollData | null | undefined): DraftQuestion[] {
  if (!poll) return [emptyQuestion()];

  // A saved poll carries its counts; a draft from the creator does not.
  const data = "totalVotes" in poll ? toCreatePollData(poll) : poll;
  if (!data.questions?.length) return [emptyQuestion()];

  return data.questions.map((question) => ({
    key: newKey(),
    id: question.id,
    text: question.text,
    allowMultiple: question.allowMultiple === true,
    options: question.options.map((option) => ({
      key: newKey(),
      id: option.id,
      text: option.text,
    })),
  }));
}

const DEFAULT_DAYS = 7;

export function PollCreator({
  initialPoll,
  onCancel,
  onSave,
  ownerLabel = "Post",
}: PollCreatorProps) {
  const [questions, setQuestions] = useState<DraftQuestion[]>(() => toDraft(initialPoll));

  const [voterVisibility, setVoterVisibility] = useState<PollVoterVisibility>(
    () =>
      (initialPoll && "voterVisibility" in initialPoll && initialPoll.voterVisibility) ||
      DEFAULT_VOTER_VISIBILITY,
  );

  const [endDate, setEndDate] = useState<Date>(() => {
    if (initialPoll && "endsAt" in initialPoll && initialPoll.endsAt) {
      return toDate(initialPoll.endsAt);
    }
    const date = new Date();
    date.setDate(date.getDate() + DEFAULT_DAYS);
    return date;
  });

  const [dateValue, setDateValue] = useState(() => toInputDate(endDate));
  const [timeValue, setTimeValue] = useState(() => toInputTime(endDate));

  const applyDateTime = (nextDate: string, nextTime: string) => {
    const parsed = new Date(`${nextDate}T${nextTime || "00:00"}:00`);
    if (!Number.isNaN(parsed.getTime())) setEndDate(parsed);
  };

  const updateQuestion = (index: number, patch: Partial<DraftQuestion>) => {
    setQuestions((current) =>
      current.map((question, i) => (i === index ? { ...question, ...patch } : question)),
    );
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;

    setQuestions((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleOptionDragEnd = (questionIndex: number, result: { source: { index: number }; destination?: { index: number } | null }) => {
    if (!result.destination) return;

    setQuestions((current) =>
      current.map((question, i) => {
        if (i !== questionIndex) return question;

        const options = [...question.options];
        const [moved] = options.splice(result.source.index, 1);
        options.splice(result.destination!.index, 0, moved);
        return { ...question, options };
      }),
    );
  };

  const handleSave = () => {
    if (questions.length > MAX_QUESTIONS) {
      toast.error(`A poll can have at most ${MAX_QUESTIONS} questions`);
      return;
    }

    for (const [index, question] of questions.entries()) {
      if (!question.text.trim()) {
        toast.error(`Question ${index + 1} needs a title`);
        return;
      }
      const filled = question.options.filter((option) => option.text.trim());
      if (filled.length < MIN_OPTIONS) {
        toast.error(`Question ${index + 1} needs at least ${MIN_OPTIONS} options with text`);
        return;
      }
    }

    if (endDate.getTime() <= Date.now()) {
      toast.error("The end date must be in the future");
      return;
    }

    onSave({
      voterVisibility,
      questions: questions.map((question, questionIndex) => ({
        id: question.id,
        text: question.text.trim(),
        order: questionIndex,
        allowMultiple: question.allowMultiple,
        options: question.options
          .filter((option) => option.text.trim())
          .map((option, optionIndex) => ({
            id: option.id,
            text: option.text.trim(),
            order: optionIndex,
          })),
      })),
      endsAt: endDate,
    });
  };

  const isEditing = Boolean(initialPoll);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          {isEditing ? "Edit Poll" : `Add Poll to ${ownerLabel}`}
        </h3>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="p-1">
          <X size={18} />
        </Button>
      </div>

      <div className="space-y-4">
        {questions.map((question, questionIndex) => (
          <div
            key={question.key}
            className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900/40"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Question {questionIndex + 1}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="p-1"
                  title="Move up"
                  aria-label={`Move question ${questionIndex + 1} up`}
                  disabled={questionIndex === 0}
                  onClick={() => moveQuestion(questionIndex, -1)}
                >
                  <ChevronUp size={16} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="p-1"
                  title="Move down"
                  aria-label={`Move question ${questionIndex + 1} down`}
                  disabled={questionIndex === questions.length - 1}
                  onClick={() => moveQuestion(questionIndex, 1)}
                >
                  <ChevronDown size={16} />
                </Button>
                {questions.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="p-1 text-red-500 hover:text-red-700"
                    title="Remove question"
                    aria-label={`Remove question ${questionIndex + 1}`}
                    onClick={() =>
                      setQuestions((current) => current.filter((_, i) => i !== questionIndex))
                    }
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            </div>

            <TextareaControl
              id={`poll-question-${question.key}`}
              label="Question"
              placeholder="Ask a question..."
              value={question.text}
              onChange={(e) => updateQuestion(questionIndex, { text: e.target.value })}
              rows={1}
              minHeight="52px"
            />

            <div className="mt-3">
              <label className="block text-sm font-medium mb-2">Options</label>

              <DragDropContext onDragEnd={(result) => handleOptionDragEnd(questionIndex, result)}>
                <Droppable droppableId={`options-${question.key}`}>
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="flex flex-col gap-2"
                    >
                      {question.options.map((option, optionIndex) => (
                        <Draggable key={option.key} draggableId={option.key} index={optionIndex}>
                          {(dragProvided) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className="flex items-center gap-2"
                            >
                              <div
                                {...dragProvided.dragHandleProps}
                                className="cursor-grab text-gray-400"
                              >
                                <GripVertical size={16} />
                              </div>
                              <InputControl
                                id={`poll-option-${option.key}`}
                                value={option.text}
                                maxLength={MAX_OPTION_LENGTH}
                                onChange={(e) =>
                                  updateQuestion(questionIndex, {
                                    options: question.options.map((candidate, i) =>
                                      i === optionIndex
                                        ? { ...candidate, text: e.target.value }
                                        : candidate,
                                    ),
                                  })
                                }
                                placeholder={`Option ${optionIndex + 1}`}
                                className="flex-1"
                              />
                              {question.options.length > MIN_OPTIONS && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="p-1 text-red-500 hover:text-red-700"
                                  aria-label={`Remove option ${optionIndex + 1}`}
                                  onClick={() =>
                                    updateQuestion(questionIndex, {
                                      options: question.options.filter(
                                        (_, i) => i !== optionIndex,
                                      ),
                                    })
                                  }
                                >
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {question.options.length < MAX_OPTIONS && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    updateQuestion(questionIndex, {
                      options: [...question.options, emptyOption()],
                    })
                  }
                  className="mt-2 w-full flex items-center justify-center"
                >
                  <Plus size={16} className="mr-1" /> Add Option
                </Button>
              )}

              <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4"
                  checked={question.allowMultiple}
                  onChange={(e) =>
                    updateQuestion(questionIndex, { allowMultiple: e.target.checked })
                  }
                />
                Allow multiple answers
              </label>
            </div>
          </div>
        ))}

        {questions.length < MAX_QUESTIONS && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setQuestions((current) => [...current, emptyQuestion()])}
            className="w-full flex items-center justify-center"
          >
            <Plus size={16} className="mr-1" /> Add Question
          </Button>
        )}

        <div>
          <label
            htmlFor="poll-voter-visibility"
            className="block text-sm font-medium mb-1"
          >
            Who can see who voted
          </label>
          <select
            id="poll-voter-visibility"
            className="form-select w-full"
            value={voterVisibility}
            onChange={(e) => setVoterVisibility(e.target.value as PollVoterVisibility)}
          >
            <option value="anonymous">Nobody — anonymous poll</option>
            <option value="everyone">Everyone who can see the poll</option>
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {voterVisibility === "anonymous"
              ? "Only the counts are shown. Not even you can see who picked what."
              : "Anyone can open the list of who picked each option, and voters are told so before they answer."}
          </p>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium mb-1">Poll End Date &amp; Time</label>

          <div className="flex flex-row gap-2 justify-between">
            <InputControl
              id="poll-end-date"
              type="date"
              className="w-full"
              value={dateValue}
              onChange={(e) => {
                setDateValue(e.target.value);
                applyDateTime(e.target.value, timeValue);
              }}
              min={toInputDate(new Date())}
            />
            <InputControl
              id="poll-end-time"
              type="time"
              className="w-full"
              value={timeValue}
              onChange={(e) => {
                setTimeValue(e.target.value);
                applyDateTime(dateValue, e.target.value);
              }}
            />
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            Poll will end on {endDate.toLocaleDateString()} at{" "}
            {endDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            {questions.length > 1 && ` · ${questions.length} questions`}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            {isEditing ? "Save Poll" : `Add Poll to ${ownerLabel}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function toInputDate(date: Date): string {
  // Local date, not the UTC one `toISOString` would give.
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function toInputTime(date: Date): string {
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${hours}:${minutes}`;
}

